import { css } from '@emotion/react'
import api from 'api'
import { ImpressaoAtendimentoInput } from 'api/ImpressaoAtendimentoIndividual'
import { ImpressaoCadernetaVacinacaoInput } from 'api/impressaoCadernetaVacinacao'
import { AxiosResponse } from 'axios'
import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  Heading,
  HeadingSection,
  HFlow,
  Icon,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
} from 'bold-ui'
import { gray } from 'bold-ui/lib/styles/colors'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useSession } from 'components/auth/useSession'
import { DateTime } from 'components/date'
import { getFirstGraphqlError, handleValidationError, isValidationError, useErrorHandler } from 'components/error'
import { CheckboxField, ErrorField, Form, FormRenderProps, FormValueSpy, PecFormApi, RadioField } from 'components/form'
import { PageContent } from 'components/layout/PageContent'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { confirm } from 'components/modals/confirm'
import { useFlags } from 'config/useFlagsContext'
import { useApolloClient } from 'graphql/hooks'
import {
  useCancelarAtendimentoMutation,
  useRetificarAtendimentoVacinacaoMutation,
  useSalvarAtendimentoVacinacaoMutation,
  useSalvarAtendimentoVacinacaoParaAprovacaoMutation,
} from 'graphql/hooks.generated'
import {
  CondicaoMaternalDbEnum,
  StatusRevisaoAtendimento,
  TipoAtendimentoProfissional,
  TipoEstabelecimentoEnum,
  TipoRegistroVacinacaoEnum,
} from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { HandleImpressaoPathProps, reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { useServerTime } from 'hooks/useServerTime'
import { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { isCboAcs } from 'types/NivelCbo'
import { sexoByIdentidadeGenero } from 'util/sexoByIdentidadeGenero'
import { useVerificarAgendamentosConflitantes } from 'view/agenda/hooks/useVerificarAgendamentosConflitantes'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import {
  convertAtendimentoVacinacaoInputToRetificacaoAtendimentoVacinacaoInput,
  convertAtendimentoVacinacaoModelToInput,
} from 'view/atendimentos/atendimento-vacinacao/graphql/converter-atendimentoVacinacao'
import {
  AtendimentoVacinacaoState,
  meta,
  RegistroVacinacaoFormModel,
} from 'view/atendimentos/atendimento-vacinacao/model'
import {
  getDescricaoTituloCalendario,
  getGrupoAlvoVacinacao,
} from 'view/atendimentos/atendimento-vacinacao/utils-vacinacao'
import {
  validateCpfCns,
  validateFinalizarAtendimento,
} from 'view/atendimentos/atendimento-vacinacao/validator-atendimentoVacinacao'
import { AgendamentoDia } from 'view/atendimentos/types/AgendamentoDia'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { computeFinalizarAtendimentoButtonLabel } from 'view/atendimentos/utils/computeFinalizarAtendimentoButtonLabel'
import { CidadaoTabSectionModel } from 'view/cidadao/visualizacao/types/model-cidadaoTabSection'
import { CheckJustificativaAcessoProntuario } from 'view/prontuario/justificativa/CheckJustificativaAcessoProntuario'
import { tipoAtendimentoRetificacaoRecord } from 'view/retificacao-atendimento/model-retificacaoAtendimento'

import { atendimentoFooterMessage } from '../atendimentoFooterMessage'
import { atendimentoImpressaoMessage } from '../atendimentoImpressaoMessage'
import { atendimentoName } from '../atendimentoName'
import { PeriodoGestacaoModel } from '../components/modals/types/PeriodoGestacaoModel'
import { useAtendimentoCallbackUrl } from '../hooks/useAtendimentoCallbackUrl'
import { useAtendimentoHandleRetificar } from '../hooks/useAtendimentoHandleRetificar'
import { AgendarConsultaPanel } from '../soap/finalizacao/components/agendar-consulta/AgendarConsultaPanel'
import { convertAgendarConsultasToAgendamentosConflitantesInput } from '../soap/finalizacao/components/agendar-consulta/converter-agendarConsultaAtendimento'
import { DesfechoAtendimentoPanel } from '../soap/finalizacao/components/DesfechoAtendimentoPanel'
import { DeclaracaoComparecimentoButton } from '../soap/plano/atestados/DeclaracaoComparecimentoBox'
import { createVacinacaoCalculator } from './calculator-vacinacao'
import { retificacaoVacinacaoConfirmModal } from './retificacaoVacinacaoConfirmModal'
import { VacinacaoCalendarioReadOnlyView } from './VacinacaoCalendarioReadOnlyView'
import { createVacinacaoErrorModal, VacinacaoCalendarioView } from './VacinacaoCalendarioView'
import { atendimentoVacinacaoValidator } from './validator-vacinacao'

interface VacinacaoViewProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  dataAtendimento: Instant
  tipoAtendimentoProfissional: TipoAtendimentoProfissional
  agendamentosDia?: ReadonlyArray<AgendamentoDia>
  gestante?: boolean
  puerpera?: boolean
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  initialValues?: AtendimentoVacinacaoState
  clearCache(updateState?: boolean): void
  updateCache?: Dispatch<SetStateAction<AtendimentoVacinacaoState | SoapState>>
  cacheState?: AtendimentoVacinacaoState | SoapState
  tipoEstabelecimento: TipoEstabelecimentoEnum
  lastSaved?: Date
  canAccessAgenda?: boolean
  isAtendimentoVacinacao?: boolean
  isAtendimentoObservacao?: boolean
  isObservacaoAndResponsavel?: boolean
  isObservacaoAndAuxiliar?: boolean
  statusRevisaoAtendimento?: StatusRevisaoAtendimento
  atendimentoProfissionalId: string
  isRetificacao?: boolean
}

export type EstadoBotaoCalendario = 'CALENDARIO_VACINAL' | 'OUTROS_IMUNOS'

// type guard para poder acessar valores dependendo do state que foi provido no parametro
const isAtendimentoVacinacaoState = (state: any): state is AtendimentoVacinacaoState => {
  return state?.registroVacinacao !== undefined
}

const disableImprimirAtendimento = (registros: ReadonlyArray<RegistroVacinacaoFormModel>) =>
  !registros.find(
    (item) => item.tipoRegistroVacinacao === TipoRegistroVacinacaoEnum.APLICACAO && !item.isRegistroAnterior
  )

export function VacinacaoView(props: Readonly<VacinacaoViewProps>) {
  const { cacheState, ...propsValues } = props

  const [initialValuesVacinacao] = useState(
    isAtendimentoVacinacaoState(cacheState) ? cacheState : cacheState?.vacinacao
  ) // usado pra setar os valores iniciais do Form apenas na primeira vez que renderizar

  return (
    <div>
      <VacinacaoForm {...propsValues} initialValues={initialValuesVacinacao} cacheState={cacheState} />
    </div>
  )
}

const gestantePuerperaCarregadosText = (gestante: boolean, puerpera: boolean) =>
  gestante
    ? 'Possui gestação ativa na Lista de problemas / condições, registrada em atendimento anterior.'
    : puerpera
    ? 'Possui condição de puerpério na Lista de problemas / condições, registrada em atendimento anterior.'
    : undefined

const generateUrlToCadastroCidadao = (
  cidadaoId: ID,
  atendimentoId: ID,
  registroTardio: boolean,
  isAtendimentoVacinacao: boolean
) => {
  const baseUrl = `${CIDADAO_PATH}/${cidadaoId}/edit`

  const callbackUrl = registroTardio
    ? 'registroTardio'
    : isAtendimentoVacinacao
    ? 'lista-atendimento/atendimento-vacinacao'
    : 'lista-atendimento/atendimento'

  return `${baseUrl}?callbackUrl=${callbackUrl}&callbackParams=${atendimentoId}`
}

export const VacinacaoForm = (props: VacinacaoViewProps) => {
  const {
    prontuarioId,
    initialValues,
    cidadao,
    dataAtendimento,
    agendamentosDia,
    atendimentoId,
    clearCache,
    updateCache,
    gestante,
    puerpera,
    tipoEstabelecimento,
    lastSaved,
    cacheState,
    canAccessAgenda = false,
    isAtendimentoVacinacao = true,
    tipoAtendimentoProfissional,
    isAtendimentoObservacao,
    isObservacaoAndResponsavel,
    isObservacaoAndAuxiliar,
    statusRevisaoAtendimento,
    atendimentoProfissionalId,
    isRetificacao = false,
  } = props

  const [estadoBotaoCalendario, setEstadoBotaoCalendario] = useState<EstadoBotaoCalendario>('CALENDARIO_VACINAL')
  const [enviarParaAprovacao] = useSalvarAtendimentoVacinacaoParaAprovacaoMutation()
  const [cancelar, { loading: isLoadingCancelar }] = useCancelarAtendimentoMutation()
  const [finalizar] = useSalvarAtendimentoVacinacaoMutation()
  const { verificarAgendamentosConflitantes } = useVerificarAgendamentosConflitantes()
  const [retificarAtendimentoVacinacao] = useRetificarAtendimentoVacinacaoMutation()
  const { isRegistroTardio } = useAtendimentoContext() || {}
  const { HORARIO_FINAL_AGENDAMENTO_ENABLED } = useFlags()

  const apollo = useApolloClient()

  const [openDosesAnterioresModal, setOpenDosesAnterioresModal] = useState(false)

  const handleRejection = useErrorHandler()
  const grupoAlvoCidadao = getGrupoAlvoVacinacao(cidadao.dataNascimento, dataAtendimento)
  const sexoIdentidadeGenero = sexoByIdentidadeGenero(cidadao.sexo, cidadao.identidadeGeneroDbEnum)
  const showGravidaPuerperaRadioField = sexoIdentidadeGenero === 'FEMININO' || sexoIdentidadeGenero === 'INDETERMINADO'
  const gestantePuerperaCarregados = useMemo(() => gestantePuerperaCarregadosText(gestante, puerpera), [
    gestante,
    puerpera,
  ])

  const { isEstagio } = useSession()

  const tipoAtendimentoText = atendimentoName(tipoAtendimentoProfissional, isObservacaoAndAuxiliar)
  const finalizarAtendimentoButtonLabel = computeFinalizarAtendimentoButtonLabel({
    isEstagio,
    statusRevisaoAtendimento,
    tipoAtendimentoText,
    isRetificacao,
  })

  const [mostrarCalendarioCompleto, setMostrarCalendarioCompleto] = useState<boolean>(false)

  const theme = useTheme()
  const style = useMemo(() => styles(theme), [theme])

  const history = useHistory()
  const alert = useAlert()
  const { getServerTimeNow } = useServerTime()
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()

  const { goToCallbackUrl } = useAtendimentoCallbackUrl({
    atendimentoId,
    cidadaoId: cidadao.id,
    onGoToCallbackUrl: () => clearCache(false),
    isRetificacao,
    isRegistroTardio,
  })

  const handleClickAtualizarCadastroCidadao = useCallback(() => {
    const url = generateUrlToCadastroCidadao(cidadao.id, atendimentoId, isRegistroTardio, isAtendimentoVacinacao)
    history.push(url)
  }, [atendimentoId, cidadao.id, history, isAtendimentoVacinacao, isRegistroTardio])

  const errorCpfCnsRequired = useMemo(
    () => validateCpfCns(cidadao.cns || cidadao.cpf, handleClickAtualizarCadastroCidadao, isAtendimentoVacinacao),
    [cidadao.cns, cidadao.cpf, handleClickAtualizarCadastroCidadao, isAtendimentoVacinacao]
  )
  useEffect(() => errorCpfCnsRequired?.hasError && createVacinacaoErrorModal(errorCpfCnsRequired), [
    errorCpfCnsRequired,
  ])

  const [formInitialized, setFormInitialized] = useState(false)
  useEffect(() => {
    setFormInitialized(true)
  }, [])

  const printAtendimentoVacinacao = useCallback(
    (itemToPrint: ImpressaoAtendimentoInput) => {
      handleImpressaoPath({
        apiPath: api.atendimentoVacinacao.imprimirAtendimentoVacinacao,
        values: itemToPrint,
        file: `AtendimentoVacinacao.pdf`,
      }).catch(handleImpressaoError)
    },
    [handleImpressaoError, handleImpressaoPath]
  )

  const calculators = useMemo(() => createVacinacaoCalculator(apollo, tipoEstabelecimento), [
    apollo,
    tipoEstabelecimento,
  ])

  const handleFormChange = useCallback(
    (values: AtendimentoVacinacaoState) => {
      /* Avoids the error of updating a component while rendering a different component. */

      // TODO (NP) rever relacoes com o atendimento individual
      if (formInitialized) {
        if (isAtendimentoVacinacaoState(cacheState)) {
          values.lastSaved = lastSaved
          updateCache(values)
        } else {
          updateCache({ ...cacheState, lastSaved: lastSaved, vacinacao: values })
        }
      }
    },
    [cacheState, formInitialized, lastSaved, updateCache]
  )
  const { analytics } = useFirebase()

  const footerMessages = atendimentoFooterMessage(
    tipoAtendimentoProfissional,
    isAtendimentoObservacao,
    isObservacaoAndResponsavel,
    isRetificacao
  )
  const validarFinalizacao = useCallback(
    (values) => {
      const error = validateFinalizarAtendimento(
        { registrosAplicadosAtendimento: values.registroVacinacao },
        cidadao.cns || cidadao.cpf,
        handleClickAtualizarCadastroCidadao,
        isAtendimentoVacinacao
      )

      if (error.hasError) createVacinacaoErrorModal(error)

      return error.hasError
    },
    [cidadao.cns, cidadao.cpf, handleClickAtualizarCadastroCidadao, isAtendimentoVacinacao]
  )

  const executarFluxoAprovacao = useCallback(
    async (values) => {
      const response = await enviarParaAprovacao({
        variables: {
          input: {
            atendimentoInput: convertAtendimentoVacinacaoModelToInput(
              atendimentoId,
              values,
              HORARIO_FINAL_AGENDAMENTO_ENABLED
            ),
            rascunho: JSON.stringify(values),
          },
        },
      })

      if (response.data.salvarAtendimentoVacinacaoParaAprovacao) {
        alert('success', 'Atendimento de vacinação enviado para aprovação com sucesso.')
        clearCache(false)
        history.push('/lista-atendimento')
      }
    },
    [enviarParaAprovacao, atendimentoId, HORARIO_FINAL_AGENDAMENTO_ENABLED, alert, clearCache, history]
  )

  const executarFluxoFinalizar = useCallback(
    async (values) => {
      const response = await finalizar({
        variables: {
          input: convertAtendimentoVacinacaoModelToInput(atendimentoId, values, HORARIO_FINAL_AGENDAMENTO_ENABLED),
        },
      })

      if (values.imprimirAtendimento) {
        printAtendimentoVacinacao({
          atendimentoProfissionalId: response.data.salvarAtendimentoVacinacao.atendimentoProfissional.id,
        })
      }

      alert('success', 'Atendimento de vacinação finalizado com sucesso.')
      analytics.logEvent('finalizar_atendimento', { Tipo_de_atendimento: 'vacinação' })
      history.push('/lista-atendimento')
      clearCache(false)
    },
    [
      finalizar,
      atendimentoId,
      HORARIO_FINAL_AGENDAMENTO_ENABLED,
      alert,
      analytics,
      history,
      clearCache,
      printAtendimentoVacinacao,
    ]
  )

  const { handleRetificar } = useAtendimentoHandleRetificar({
    tipoAtendimentoProfissional,
    printItem: printAtendimentoVacinacao,
    onFinalizar: retificarAtendimentoVacinacao,
    goToCallbackUrl,
  })

  const handleCancelar = useCallback(() => {
    return confirm({
      title: footerMessages.confirmTitle,
      body: footerMessages.body,
      confirmLabel: 'Sim, cancelar',
      cancelLabel: 'Não, continuar aqui',
      icon: {
        name: 'infoCircleOutline',
        fill: 'normal',
      },
      onConfirm: () => {
        if (isRetificacao) {
          analytics.logEvent('cancelar_RET', {
            tipo_de_atendimento: tipoAtendimentoRetificacaoRecord[tipoAtendimentoProfissional].title,
          })
          goToCallbackUrl()
        } else {
          cancelar({ variables: { atendimentoId } })
            .then(() => {
              goToCallbackUrl()
              analytics.logEvent('cancelar_atendimento', {
                tipo_de_atendimento: 'vacinação',
              })
            })
            .catch(handleRejection)
        }
      },
    })()
  }, [
    footerMessages.confirmTitle,
    footerMessages.body,
    isRetificacao,
    analytics,
    tipoAtendimentoProfissional,
    goToCallbackUrl,
    cancelar,
    atendimentoId,
    handleRejection,
  ])

  const handleFinalizarRetificacao = useCallback(
    (formApi: PecFormApi<AtendimentoVacinacaoState>) => {
      const { initialValues, values } = formApi.getState()

      const atendimentoVacinacaoInput = convertAtendimentoVacinacaoModelToInput(
        atendimentoId,
        values,
        HORARIO_FINAL_AGENDAMENTO_ENABLED
      )

      const retificacaoAtendimentoVacinacaoInput = convertAtendimentoVacinacaoInputToRetificacaoAtendimentoVacinacaoInput(
        atendimentoVacinacaoInput,
        atendimentoProfissionalId,
        values.retificacaoAtendimento
      )

      return retificacaoVacinacaoConfirmModal({
        initialValues: initialValues as AtendimentoVacinacaoState,
        currentValues: values,
        nomeCidadao: (cidadao.nomeSocial ?? cidadao.nome).titleCase(),
        onRetificar: () => handleRetificar(retificacaoAtendimentoVacinacaoInput, values.imprimirAtendimento),
        onRetificarSemAlteracoes: goToCallbackUrl,
        tipoAtendimentoProfissional,
        canHaveCondicaoMaternal: !showGravidaPuerperaRadioField,
      })
    },
    [
      atendimentoId,
      HORARIO_FINAL_AGENDAMENTO_ENABLED,
      atendimentoProfissionalId,
      cidadao.nomeSocial,
      cidadao.nome,
      goToCallbackUrl,
      tipoAtendimentoProfissional,
      showGravidaPuerperaRadioField,
      handleRetificar,
    ]
  )

  const handleFinalizarError = useCallback(
    (error) => {
      const firstError = getFirstGraphqlError(error)
      if (isValidationError(firstError)) {
        // eslint-disable-next-line no-console
        console.error(
          `Erro ao finalizar atendimento ${tipoAtendimentoText}`,
          JSON.stringify(handleValidationError(firstError))
        )
      }
      throw error
    },
    [tipoAtendimentoText]
  )

  const handleFinalizar = useCallback(
    async (values: AtendimentoVacinacaoState, formApi: PecFormApi<AtendimentoVacinacaoState>) => {
      const hasError = validarFinalizacao(values)
      if (hasError) return

      if (isEstagio) {
        return executarFluxoAprovacao(values)
      }

      if (values.finalizacaoVacinacao?.agendamentoConsultas) {
        const confirmou = await verificarAgendamentosConflitantes(
          cidadao.id,
          convertAgendarConsultasToAgendamentosConflitantesInput(values.finalizacaoVacinacao.agendamentoConsultas),
          cidadao.nomeSocial ?? cidadao.nome
        )
        if (!confirmou) return
      }

      /*
      // TODO (NP) removido temporariamente

      if (!isAtendimentoVacinacao) {
        return finalizarIndividual({
          variables: {
            input: converAtendimentoIndividualModelToInput(atendimentoId, cacheState, getServerTimeNow()),
          },
        }).then((response) => {
          values.imprimirAtendimento &&
            printAtendimentoIndividual({
              atendimentoProfissionalId: response.data.finalizarAtendimentoIndividual.id,
              isImpressaoHistorico: false,
            })
          alert('success', `Atendimento individual finalizado com sucesso.`)
          analytics.logEvent('finalizar_atendimento', { tipo_de_atendimento: 'vacinação' })
          history.push('/lista-atendimento')
          clearCache()
        })
      } else {
    */

      try {
        if (isRetificacao) {
          return await handleFinalizarRetificacao(formApi)
        } else {
          return await executarFluxoFinalizar(values)
        }
      } catch (error) {
        handleFinalizarError(error)
      }
    },
    [
      cidadao,
      isEstagio,
      verificarAgendamentosConflitantes,
      executarFluxoAprovacao,
      executarFluxoFinalizar,
      validarFinalizacao,
      handleFinalizarError,
      handleFinalizarRetificacao,
      isRetificacao,
    ]
  )

  const renderVacinacao = useCallback(
    (formProps: FormRenderProps<AtendimentoVacinacaoState>) => {
      const { handleSubmit, values } = formProps
      const disableImpressao = disableImprimirAtendimento(values.registroVacinacao)
      const calendarioGestante =
        formProps.values.condicoesVacinacao?.condicao === CondicaoMaternalDbEnum.GESTANTE ? ' / gestante' : ''

      return (
        <Fragment>
          <FormValueSpy<AtendimentoVacinacaoState> onChange={handleFormChange} />
          {isAtendimentoVacinacao && (
            <HeadingSection level={2} title='Condições' style={style.headingSection}>
              {gestantePuerperaCarregados && (
                <Alert inline style={{ marginBottom: '1rem' }} type='info'>
                  {gestantePuerperaCarregados}
                </Alert>
              )}

              <div css={showGravidaPuerperaRadioField && style.box}>
                <HFlow>
                  {showGravidaPuerperaRadioField && (
                    <Fragment>
                      <FormControl label='Maternal' required>
                        <VFlow vSpacing={0.5}>
                          <HFlow>
                            <RadioField
                              name={meta.condicoesVacinacao.condicao}
                              value={CondicaoMaternalDbEnum.GESTANTE}
                              label='Gestante'
                            />
                            <RadioField
                              name={meta.condicoesVacinacao.condicao}
                              value={CondicaoMaternalDbEnum.PUERPERA}
                              label='Puérpera'
                            />
                            <RadioField
                              name={meta.condicoesVacinacao.condicao}
                              value={CondicaoMaternalDbEnum.NENHUMA}
                              label='Nenhuma'
                              css={style.separator}
                            />
                          </HFlow>
                          <ErrorField name={meta.condicoesVacinacao.condicao} />
                        </VFlow>
                      </FormControl>
                      <div css={style.separator} />
                    </Fragment>
                  )}
                  <FormControl label='Outras'>
                    <CheckboxField name={meta.condicoesVacinacao.viajante} label='Viajante' />
                  </FormControl>
                </HFlow>
              </div>
            </HeadingSection>
          )}
          <HeadingSection level={2} title='Vacinação'>
            <VFlow>
              {!(cidadao.cpf || cidadao.cns) && (
                <Alert type='warning'>
                  {`É necessário cadastrar um CPF ou CNS para o cidadão. É obrigatório um dos documentos para finalizar o
                  ${isAtendimentoVacinacao ? 'atendimento de vacinação.' : 'atendimento.'}`}
                </Alert>
              )}
              <Alert type='info' style={{ padding: '5px' }}>
                {isRegistroTardio ? (
                  'Registros de vacinação devem ser realizados pela transcrição de caderneta através da lista de atendimentos.'
                ) : (
                  <Fragment>
                    Para imprimir todos os registros salvos em atendimentos anteriores, clique em "Imprimir caderneta de
                    vacinação".
                    <br />
                    {`Para imprimir os registros realizados neste atendimento, selecione a opção "Imprimir atendimento
                          ao finalizar"${isAtendimentoVacinacao ? '.' : ' na aba SOAP.'}`}
                  </Fragment>
                )}
              </Alert>
              <HFlow justifyContent='space-between'>
                <TipoCalendarioImunosButtonGroup
                  estadoBotaoCalendario={estadoBotaoCalendario}
                  setEstadoBotaoCalendario={setEstadoBotaoCalendario}
                  calendarioVacinalTitle={
                    mostrarCalendarioCompleto
                      ? 'Calendário vacinal completo'
                      : `Calendário vacinal ${getDescricaoTituloCalendario(grupoAlvoCidadao)}${calendarioGestante}`
                  }
                />
                <Button
                  kind='normal'
                  size='small'
                  skin='default'
                  style={style.printCadernetaButton}
                  onClick={() =>
                    impressaoCadernetaVacinacao(
                      handleImpressaoPath,
                      { prontuarioId, atendimentoId },
                      handleImpressaoError
                    )
                  }
                >
                  <Icon icon='printerFilled' style={style.printBtn} />
                  Imprimir caderneta de vacinação
                </Button>
              </HFlow>
              {!isAtendimentoVacinacao && !isRegistroTardio && (
                <Button
                  kind='primary'
                  size='small'
                  skin='default'
                  style={css`
                    width: 16rem;
                  `}
                  onClick={() => setOpenDosesAnterioresModal(true)}
                >
                  Adicionar transcrições de caderneta
                </Button>
              )}
              <VacinacaoCalendarioView
                cidadao={cidadao}
                condicoesVacinacao={formProps.values.condicoesVacinacao}
                openDosesAnterioresModal={openDosesAnterioresModal}
                closeDosesAnterioresModal={() => setOpenDosesAnterioresModal(false)}
                dataAtendimento={dataAtendimento}
                estadoBotaoCalendario={estadoBotaoCalendario}
                grupoAlvoCidadao={grupoAlvoCidadao}
                prontuarioId={prontuarioId}
                registros={formProps.values.registroVacinacao}
                formVacinacao={formProps.form}
                mostrarCalendarioCompleto={mostrarCalendarioCompleto}
                setMostrarCalendarioCompleto={setMostrarCalendarioCompleto}
                isAcompanhamentoVacinal={!isAtendimentoVacinacao}
                sexoIdentidadeGenero={sexoIdentidadeGenero}
                atendimentoProfissionalId={atendimentoProfissionalId}
                isRetificacao={isRetificacao}
              />
              {isAtendimentoVacinacao && !isRetificacao && (
                <Fragment>
                  <div css={style.desfechoContainer}>
                    <span css={style.desfechoInitialIcon}>
                      <Icon
                        icon='checkCircleFilled'
                        size={1.5}
                        style={css`
                          height: 100%;
                        `}
                      />
                    </span>
                    <Heading level={2} style={style.desfechoTitle}>
                      Finalização do atendimento
                    </Heading>
                  </div>
                  <VFlow
                    style={css`
                      padding-top: 1rem;
                    `}
                  >
                    <DeclaracaoComparecimentoButton atendimentoId={atendimentoId} />
                    <DesfechoAtendimentoPanel
                      name={meta.finalizacaoVacinacao.desfechoAtendimento}
                      agendamentosDia={agendamentosDia}
                      atendimentoId={atendimentoId}
                      cidadaoId={cidadao?.id}
                      isRegistroTardio={isRegistroTardio}
                    />
                    <AgendarConsultaPanel
                      name={meta.finalizacaoVacinacao.agendamentoConsultas}
                      hasPainelLateral={false}
                      showHorarioFinal={HORARIO_FINAL_AGENDAMENTO_ENABLED}
                      // TODO (RNG): Temporário. Será implementado na #31342
                      mostrarCheckboxesComprovantes={false}
                    />
                  </VFlow>
                </Fragment>
              )}
            </VFlow>
          </HeadingSection>
          <div css={style.actions}>
            <div css={style.sideBar} />
            <HFlow justifyContent='space-between'>
              <Alert inline type='success' styles={{ wrapper: style.draftAlertWrapper }}>
                Rascunho salvo automaticamente às <DateTime format='HH:mm' value={lastSaved} />
              </Alert>

              <HFlow>
                {isAtendimentoVacinacao && !isEstagio && (
                  <Tooltip
                    text={disableImpressao && 'Para habilitar a opção de impressão, realize um registro de aplicação.'}
                  >
                    <span>
                      <CheckboxField
                        name={meta.imprimirAtendimento}
                        label={atendimentoImpressaoMessage(isRetificacao)}
                        style={style.print}
                        disabled={disableImpressao}
                        onClick={() => analytics.logEvent('click_imprimir_finalizar')}
                      />
                    </span>
                  </Tooltip>
                )}
                <Button kind='normal' onClick={handleCancelar} loading={isLoadingCancelar} disabled={isLoadingCancelar}>
                  {footerMessages.labelButton}
                </Button>
                {isAtendimentoVacinacao && (
                  <Button kind='primary' onClick={handleSubmit}>
                    {finalizarAtendimentoButtonLabel}
                  </Button>
                )}
              </HFlow>
            </HFlow>
          </div>
        </Fragment>
      )
    },
    [
      handleFormChange,
      isAtendimentoVacinacao,
      style.headingSection,
      style.box,
      style.separator,
      style.printCadernetaButton,
      style.printBtn,
      style.desfechoContainer,
      style.desfechoInitialIcon,
      style.desfechoTitle,
      style.actions,
      style.sideBar,
      style.draftAlertWrapper,
      style.print,
      gestantePuerperaCarregados,
      showGravidaPuerperaRadioField,
      cidadao,
      isRegistroTardio,
      estadoBotaoCalendario,
      mostrarCalendarioCompleto,
      grupoAlvoCidadao,
      openDosesAnterioresModal,
      dataAtendimento,
      prontuarioId,
      sexoIdentidadeGenero,
      atendimentoProfissionalId,
      isRetificacao,
      atendimentoId,
      agendamentosDia,
      HORARIO_FINAL_AGENDAMENTO_ENABLED,
      lastSaved,
      isEstagio,
      handleCancelar,
      isLoadingCancelar,
      footerMessages.labelButton,
      finalizarAtendimentoButtonLabel,
      handleImpressaoPath,
      handleImpressaoError,
      analytics,
    ]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 60vh;
      `}
    >
      <PageContent fluid type='filled'>
        <Form<AtendimentoVacinacaoState>
          render={renderVacinacao}
          onSubmit={handleFinalizar}
          decorators={[calculators]}
          initialValues={initialValues}
          validate={atendimentoVacinacaoValidator(
            canAccessAgenda,
            getServerTimeNow,
            showGravidaPuerperaRadioField,
            HORARIO_FINAL_AGENDAMENTO_ENABLED
          )}
        />
      </PageContent>
    </div>
  )
}

interface TipoCalendarioImunosButtonGroupProps {
  estadoBotaoCalendario: EstadoBotaoCalendario
  setEstadoBotaoCalendario: Dispatch<SetStateAction<EstadoBotaoCalendario>>
  calendarioVacinalTitle: string
}

const TipoCalendarioImunosButtonGroup = ({
  estadoBotaoCalendario,
  setEstadoBotaoCalendario,
  calendarioVacinalTitle,
}: TipoCalendarioImunosButtonGroupProps) => {
  return (
    <ButtonGroup>
      <Button
        kind={estadoBotaoCalendario === 'CALENDARIO_VACINAL' ? 'primary' : 'normal'}
        size='small'
        skin='default'
        onClick={() => setEstadoBotaoCalendario('CALENDARIO_VACINAL')}
        data-testid='Button.CalendarioVacinal'
      >
        {calendarioVacinalTitle}
      </Button>
      <Button
        kind={estadoBotaoCalendario === 'OUTROS_IMUNOS' ? 'primary' : 'normal'}
        size='small'
        skin='default'
        onClick={() => setEstadoBotaoCalendario('OUTROS_IMUNOS')}
        data-testid='Button.OutrosImunologicos'
      >
        Outras doses e imunobiológicos
      </Button>
    </ButtonGroup>
  )
}

interface VacinacaoReadOnlyViewProps {
  cidadao: CidadaoTabSectionModel
  dataReferencia: Instant
}

export const VacinacaoReadOnlyView = (props: VacinacaoReadOnlyViewProps) => {
  const { cidadao, dataReferencia } = props

  const grupoAlvoCidadao = getGrupoAlvoVacinacao(cidadao.dataNascimento, dataReferencia)
  const sexoIdentidadeGenero = sexoByIdentidadeGenero(cidadao.sexo, cidadao.identidadeGeneroDbEnum)
  const [mostrarCalendarioCompleto, setMostrarCalendarioCompleto] = useState<boolean>(false)
  const [estadoBotaoCalendario, setEstadoBotaoCalendario] = useState<EstadoBotaoCalendario>('CALENDARIO_VACINAL')

  const match = useRouteMatch()
  const theme = useTheme()
  const style = styles(theme)

  const { acesso } = useAcessoLotacaoOrEstagio()
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 60vh;
      `}
    >
      <PageContent fluid type='filled'>
        <CheckJustificativaAcessoProntuario
          cidadaoId={cidadao.id}
          prontuarioId={cidadao.prontuario.id}
          basePath={match.path.substring(0, match.path.lastIndexOf('/'))}
          disabled={isCboAcs(acesso.cbo.cbo2002)}
        >
          <HeadingSection level={2} title='Vacinação'>
            <VFlow>
              <HFlow justifyContent='space-between'>
                <TipoCalendarioImunosButtonGroup
                  estadoBotaoCalendario={estadoBotaoCalendario}
                  setEstadoBotaoCalendario={setEstadoBotaoCalendario}
                  calendarioVacinalTitle={
                    mostrarCalendarioCompleto
                      ? 'Calendário vacinal completo'
                      : 'Calendário vacinal' +
                        getDescricaoTituloCalendario(grupoAlvoCidadao) +
                        (cidadao.prontuario.preNatalAtivo ? ' / gestante' : '')
                  }
                />
                <Button
                  kind='normal'
                  size='small'
                  skin='default'
                  style={style.printCadernetaButton}
                  onClick={() =>
                    impressaoCadernetaVacinacao(
                      handleImpressaoPath,
                      { prontuarioId: cidadao.prontuario.id },
                      handleImpressaoError
                    )
                  }
                >
                  <Icon icon='printerFilled' style={style.printBtn} />
                  Imprimir caderneta de vacinação
                </Button>
              </HFlow>
              <VacinacaoCalendarioReadOnlyView
                cidadao={cidadao}
                dataReferencia={dataReferencia}
                grupoAlvoCidadao={grupoAlvoCidadao}
                prontuarioId={cidadao?.prontuario?.id}
                mostrarCalendarioCompleto={mostrarCalendarioCompleto}
                estadoBotaoCalendario={estadoBotaoCalendario}
                setMostrarCalendarioCompleto={setMostrarCalendarioCompleto}
                sexoIdentidadeGenero={sexoIdentidadeGenero}
              />
            </VFlow>
          </HeadingSection>
        </CheckJustificativaAcessoProntuario>
      </PageContent>
    </div>
  )
}

const impressaoCadernetaVacinacao = (
  handleImpressao: <T>(props: HandleImpressaoPathProps<T>) => Promise<void | AxiosResponse<Blob | ArrayBuffer>>,
  itemToPrint: ImpressaoCadernetaVacinacaoInput,
  handleImpressaoError: (error: any) => void
) => {
  handleImpressao({
    apiPath: api.cadernetaVacinacao.imprimirCadernetaVacinacao,
    values: itemToPrint,
    file: `CadernetaVacinacao.pdf`,
    dateFormat: reportFormatDate,
  }).catch(handleImpressaoError)
}

const styles = (theme: Theme) => {
  return {
    container: css`
      display: grid;
      grid-template-columns: 0.4fr 1fr;
      grid-template-rows: auto;
      grid-template-areas: 'sidebar main' 'footer footer';
    `,

    aside: css`
      width: 100%;
      max-width: 27rem;
    `,

    print: css`
      border: 0.063rem solid ${theme.pallete.gray.c80};
      border-radius: 0.125rem;
      padding: 0.75rem 1rem;
    `,

    actions: css`
      height: 5.125rem;
      position: relative;
      grid-area: footer;
      padding-top: 1rem;
      margin-bottom: 1rem;
    `,

    draftAlertWrapper: css`
      background-color: transparent;
      border-width: 0;
    `,

    headingDesfecho: css`
      padding: 0.5rem 0 0.5rem 0;
      background-color: ${theme.pallete.gray.c90};
    `,

    separator: css`
      border-left: 1px solid ${theme.pallete.divider};
    `,

    box: css`
      padding: 1rem;
      width: 29rem;
      border: 1px solid ${theme.pallete.gray.c80};
    `,

    sideBar: css`
      width: 100vw;
      position: relative;
      left: 50%;
      right: 50%;
      margin-left: -53vw;
      margin-right: -50vw;
      margin-bottom: 1rem;
      border-bottom: 1px solid ${theme.pallete.divider};
    `,

    desfechoContainer: css`
      margin: 1rem 0 0 0;
      display: flex;
      border-radius: 2;
      background: ${theme.pallete.gray.c90};
      &:hover {
        background-color: ${theme.pallete.gray.c80};
      }
    `,

    desfechoInitialIcon: css`
      background: ${gray.c40};
      color: ${theme.pallete.gray.c100};
      border-radius: 4px;
      padding: 0.2rem 0.4rem;
    `,

    desfechoTitle: css`
      flex: 1;
      padding: 0.25rem 0.75rem;
    `,

    headingSection: css`
      margin-bottom: 1rem;
    `,
    printBtn: css`
      margin-right: 7px;
      font-size: 1.1rem;
    `,
    printCadernetaButton: css`
      color: #0069d0;
      border-color: #0069d0;
    `,
  }
}
